import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, LogOut } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/EnterpriseAuthContext';
import { useUserProfile } from '@/contexts/ProfileContext';

interface InactivityWarningModalProps {
  isOpen: boolean;
  onStayActive: () => void;
}

export function InactivityWarningModal({ 
  isOpen, 
  onStayActive 
}: InactivityWarningModalProps) {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { displayName } = useUserProfile();

  const handleSignOutNow = async () => {
    await signOut('user_logout', { 
      reason: 'user_initiated_during_warning',
      warning_acknowledged: true 
    });
    navigate('/signed-out', { replace: true });
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-[400px] border-border bg-background p-8"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className="space-y-6 text-center">
          {/* Icon */}
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <Shield className="h-7 w-7 text-primary" />
          </div>

          {/* Heading */}
          <h2 className="text-xl font-semibold text-foreground">
            Still there, {displayName}?
          </h2>

          {/* Body */}
          <p className="text-sm text-muted-foreground leading-relaxed">
            You've been inactive for a little while. Click below to stay logged in, or we'll automatically sign you out in a couple of minutes to keep your account secure.
          </p>

          {/* Buttons */}
          <div className="flex flex-col gap-3 pt-2">
            <Button 
              onClick={onStayActive}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
              size="lg"
            >
              Stay Active
            </Button>
            
            <Button 
              onClick={handleSignOutNow}
              variant="outline"
              className="w-full"
              size="lg"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
